import { Component, ComponentChild, h } from 'preact'
import EmptyPage from '../empty-page/EmptyPage'
import Switcher from '../control/Switcher'
import Panel from '../panel'
import axios from 'axios'
import { common } from '../common'
import AlertComponent from '../ui-components/AlertComponent'
import Header from '../header'
import globals from '../../utils/globals'
import ButtonControl from '../button-control'
import { EditModal, DeleteModal } from './PublicTagModals'
import Table from '../table/Table'
import PaginationComponent, { PaginationProps } from '../ui-components/PaginationComponent'
import Loader from '../ui-components/Loader'

interface Tag {
    id: string,
    name: string,
    count?: string
}

interface Row {
  header?: string,
  accessor: string,
  cell: string|ComponentChild,
  colspan?: number
}

interface Props {
  tags: Array<Tag>,
  total: number,
  tagCreationSetting: boolean,
  showTagCreationSetting: boolean
}

interface State {
  tags: Array<Tag>,
  isTagCreationEnabled: boolean,
  messageType?: string|null,
  message?: string,
  modalErrorMessage?: string,
  showCreateModal: boolean,
  isLoading: boolean,
  isPaginationSet: boolean,
  pagination: PaginationProps,
  tagToEdit?: Tag,
  tagToDelete?: Tag,
}

const DEFAULT_PAGE = 1
const DEFAULT_PAGE_SIZE = 25

const DEFAULT_PAGINATION = {
  pagination: {
    page: DEFAULT_PAGE,
    pageSize: DEFAULT_PAGE_SIZE
  }
}
const PAGINATION_STORAGE_KEY = 'tagsPagination' as string
const DEFAULT_ERROR_MESSAGE = 'Something went wrong, please try again.'

class PublicTagOverview extends Component<Props, State> {
  isPublicTagManagementEnabled: boolean

  constructor (props: Props) {
    super(props)
    this.state = {
      tags: props.tags,
      isTagCreationEnabled: props.tagCreationSetting,
      messageType: null,
      message: '',
      modalErrorMessage: '',
      showCreateModal: false,
      isLoading: false,
      isPaginationSet: false,
      pagination: {
        page: DEFAULT_PAGE,
        pageSize: DEFAULT_PAGE_SIZE,
        total: props.total
      }
    }

    this.isPublicTagManagementEnabled = (common.isModuleActive('publicTagManagement'))
    this.onTagCreationToggle = this.onTagCreationToggle.bind(this)
    this.onPageChange = this.onPageChange.bind(this)
    this.onPageSizeChange = this.onPageSizeChange.bind(this)
    this.onClickEditModalInput = this.onClickEditModalInput.bind(this)

    let element: HTMLInputElement | null = document.querySelector('input#csrftoken')

    if (element !== null) {
      this.csrf = element.value
    }
  }

  csrf: string = ''

  async componentDidMount () {

    if (!window.sessionStorage.getItem(PAGINATION_STORAGE_KEY)) {
      window.sessionStorage.setItem(PAGINATION_STORAGE_KEY, JSON.stringify(DEFAULT_PAGINATION))
    } else {
      let item = window.sessionStorage.getItem(PAGINATION_STORAGE_KEY)
      const storedPagination = item ? JSON.parse(item) : null
      const { pagination: { pageSize } } = storedPagination
      const isPaginationSet = JSON.stringify(storedPagination) !== JSON.stringify(DEFAULT_PAGINATION)
      const newPagination = this.state.pagination
      newPagination.pageSize = pageSize
      this.setState({ pagination: newPagination, isPaginationSet: isPaginationSet })
    }
  }

  async onTagCreationToggle () {
    if (!this.isPublicTagManagementEnabled) {
      return
    }

    this.setState({ messageType: null })
    const successMessage = 'Setting has been updating successfully.'
    const errorMessage = 'Something went wrong, setting can\'t be updated. Please try again.'

    try {
      await axios.post('/settings/tags/toggleTagCreation', {
        isActive: !this.state.isTagCreationEnabled
      })
      this.setState({
        isTagCreationEnabled: !this.state.isTagCreationEnabled,
        message: successMessage,
        messageType: 'success'
      })
    } catch (e) {
      this.setState({ messageType: 'error', message: errorMessage })
    }
  }

  async createTag (newType: string) {
    if (!newType) {
      this.setState({ modalErrorMessage: 'Please enter tag name', isLoading: false })
      return
    }

    this.setState({ messageType: null })
    const successMessage = `Public tag '${newType}' has been added!`

    try {
      await axios.put(`/settings/tags/create`, { tagName: newType })
    } catch (err) {
      let errorMessage = err.response?.data?.errors || DEFAULT_ERROR_MESSAGE
      this.setState({ modalErrorMessage: errorMessage, isLoading: false})
      return
    }

    const { page, pageSize } = this.state.pagination
    this.fetchTags(page, pageSize)
    this.setState({
      showCreateModal: false,
      message: successMessage,
      messageType: 'success'
    })
  }

  async editTag (tagToEdit: Tag, newName: string) {
    if (!newName) {
      this.setState({ modalErrorMessage: 'Please enter tag name', isLoading: false })
      return
    }

    this.setState({ messageType: null })
    const successMessage = `Public tag '${tagToEdit.name}' has been renamed to '${newName}'!`
    if (tagToEdit.name === newName) {
      this.setState({
        tagToEdit: undefined,
        isLoading: false
      })
      return
    }

    try {
      const { page, pageSize } = this.state.pagination
      const res = await axios.post(`/settings/tags/${tagToEdit.id}`,
        {
          oldName: tagToEdit.name,
          newName: newName,
          page: page,
          pageSize: pageSize
        }
      )
      const tags = res.data?.tags || this.state.tags
      this.setState({
        tags: tags,
        isLoading: false,
        message: successMessage,
        messageType: 'success',
        tagToEdit: undefined
      })
    } catch (err) {
      let errorMessage = err.response?.data?.errors || DEFAULT_ERROR_MESSAGE
      this.setState({ modalErrorMessage: errorMessage, isLoading: false })
    }
  }

  async deleteTag (tagToDelete: Tag) {
    this.setState({ messageType: null })
    const successMessage = `Public tag '${tagToDelete.name}' has been deleted.`

    try {
      await axios.delete(`/settings/tags/${tagToDelete.id}`, {
        data: {
          csrfToken: this.csrf
        }
      })
    } catch (err) {
      this.setState({ message: DEFAULT_ERROR_MESSAGE, messageType: 'error', isLoading: false })
      return
    }
    const { page, pageSize } = this.state.pagination
    let currentPage = page || DEFAULT_PAGE
    // fetch previous page when a tag deleted from a page that has 1 tag displayed
    let fetchPage = (this.state.tags.length === 1 && currentPage !== 1 ) ? currentPage - 1 : currentPage
    this.fetchTags(fetchPage, pageSize)
    this.setState({
      isLoading: false,
      message: successMessage,
      messageType: 'success'
    })
  }

  onPageChange (page: number) {
    const pageSize = this.state.pagination.pageSize
    this.updatePagination(page, pageSize)
    this.fetchTags(page, pageSize)
  }

  onPageSizeChange (pageSize: number) {
    const page = DEFAULT_PAGE
    this.updatePagination(page, pageSize)
    this.fetchTags(page, pageSize)
  }

  updatePagination (page: number, pageSize:number | undefined) {
    const newPagination = this.state.pagination
    newPagination.page = page
    newPagination.pageSize = pageSize
    this.setState({ pagination: newPagination })
    this.saveSessionPagination(page, pageSize)
  }

  saveSessionPagination (page: number, pageSize:number | undefined) {
    let item = window.sessionStorage.getItem(PAGINATION_STORAGE_KEY)
    let storage = item ? JSON.parse(item) : null
    storage['pagination'] = { page, pageSize }
    window.sessionStorage.setItem(PAGINATION_STORAGE_KEY, JSON.stringify(storage))
  }

  async fetchTags (page = DEFAULT_PAGE, pageSize = DEFAULT_PAGE_SIZE) {
    this.setState({ isLoading: true })
    const url = `/publicTag/fetchTags?page=${page}&pageSize=${pageSize}`
    try {
      const res = await axios.get(url)
      const { tags, total } = res.data
      const newPagination = this.state.pagination
      newPagination.total = total
      newPagination.pageSize = pageSize
      newPagination.page = page
      this.setState({ tags: tags, isLoading: false, pagination: newPagination })
    } catch (err) {
      this.setState({ message: DEFAULT_ERROR_MESSAGE, messageType: 'error', isLoading: false })
      throw err
    }
  }

  onClickEditModalInput () {
    if (this.state.modalErrorMessage) {
      this.setState({ modalErrorMessage: undefined })
    }
  }

  render ({ showTagCreationSetting }: Props, { isTagCreationEnabled, messageType, message, showCreateModal, isLoading, tags, pagination, tagToEdit, tagToDelete, modalErrorMessage }: State) {
    const isVisibleFor = messageType === 'success' ? 5000 : false
    const defaultCdn = globals('defaultCdn')
    const paginationOptions = {
      pagination,
      onPageChange: this.onPageChange,
      onPageSizeChange: this.onPageSizeChange
    }
    const totalCountText = pagination.total === 1 ? 'Public tag' : 'Public tags'
    const customButtons = (tags.length > 0) ? [
      <ButtonControl
        class='qa-add-public-tag'
        variant='primary'
        title='Add public tag'
        onClick={() => this.setState({ showCreateModal: true })}
      />] : []
    const rows = tags.map((tag: Tag): Array<Row> => [{
      header: 'Public tags',
      accessor: 'tag-name',
      cell: <span className={`public-tag-${tag.id} qa-public-tag-${tag.id}`}>{tag.name}</span>
    }, {
      header: 'Actions',
      accessor: 'actions',
      cell: <span>
        <ButtonControl class={`public-tag-edit-${tag.id} qa-public-tag-edit-${tag.name} qa-public-tag-edit-${tag.id}`} title='Edit' onClick={() => this.setState({ tagToEdit: tag })} />
         <ButtonControl class={`public-tag-remove-${tag.id} qa-public-tag-remove-${tag.name} qa-public-tag-remove-${tag.id}`} title='Remove' variant={'warning'} onClick={() => this.setState({ tagToDelete: tag, messageType: null })} />
      </span>
    }])

    return (
      <div className='public-tag-overview qa-public-tag-overview'>
        <Header
          title='Public tags'
          disableCancelButton
          disableSaveButton
          customButtons={customButtons}
        />
        <div>
          {isLoading && <Loader />}
          {
            messageType ? <AlertComponent messageType={messageType} message={message} sticky renderHTML isVisibleFor={isVisibleFor} /> : null
          }
          {showTagCreationSetting &&
          <Panel title='Setting' class='public-tag-panel-with-margin qa-public-tag-creation-setting' collapsible>
            <Switcher
              on={isTagCreationEnabled}
              label='Allow end users to add new public tags'
              onChange={this.onTagCreationToggle} />
          </Panel>
          }
          {tags.length > 0
            ? <div className='control-body public-tags-overview-table qa-public-tags-overview-table'>
              <div className='public-tags__table-info'>
                <span className='public-tags__counter'>{paginationOptions.pagination.total} {totalCountText}</span>
                <PaginationComponent {...paginationOptions} />
              </div>
              <Table rows={rows} rowId={'id'} classes={['public_tag__table']} />
            </div>
            : <EmptyPage
              img_src={`${defaultCdn}/images/tags_empty_page.png`}
              header='No public tags found'
              text={'Help users find more relevant content by adding public tags.'}
              children={<ButtonControl title='Add public tag'
                class='qa-add-public-tag'
                variant='primary' onClick={() => this.setState({ showCreateModal: true })} />}
            />
          }

          {showCreateModal ? <EditModal onClose={() => this.setState({ showCreateModal: false, modalErrorMessage: undefined })}
            onClick={this.onClickEditModalInput}
            onSave={(newName: string) => this.setState({ isLoading: true }, () => this.createTag(newName))}
            error={modalErrorMessage} /> : null}
          {tagToEdit ? <EditModal tag={tagToEdit.name}
            onClose={() => this.setState({ tagToEdit: undefined, modalErrorMessage: undefined })}
            onClick={this.onClickEditModalInput}
            onSave={(newName: string) => this.setState({ isLoading: true }, () => this.editTag(tagToEdit, newName))}
            error={modalErrorMessage}
          /> : null}
          {tagToDelete ? <DeleteModal tag={tagToDelete.name}
           onClose={() => this.setState({ tagToDelete: undefined })}
           onDelete={() => this.setState({ tagToDelete: undefined, isLoading: true }, () => this.deleteTag(tagToDelete))}
          /> : null}
        </div>
      </div>
    )
  }
}

export default PublicTagOverview
